// components/shared/EqipRichTextEditor.jsx
import React, { useContext, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import EditorLoadingContext from './editorLoadingContext';

const MAX_IMAGE_BYTES = 1 * 1024 * 1024; // 1 MB per image

/**
 * Images upload handler: called for file-picker inserts, drag-drop, and automatic
 * re-uploads of pasted data URIs when automatic_uploads is true.
 * Rejects images over MAX_IMAGE_BYTES with a user-visible error.
 */
const imagesUploadHandler = (blobInfo) =>
  new Promise((resolve, reject) => {
    const sizeBytes = blobInfo.blob().size;
    if (sizeBytes > MAX_IMAGE_BYTES) {
      const sizeMB = (sizeBytes / 1024 / 1024).toFixed(1);
      reject({
        message: `Image too large (${sizeMB} MB). Maximum allowed is 1 MB. Please resize the image before inserting.`,
        remove: true,
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject({ message: 'Failed to read image file.', remove: true });
    reader.readAsDataURL(blobInfo.blob());
  });

/**
 * Scan pasted HTML for inline data-URI images and remove any that exceed the
 * size limit before they land in the editor DOM.
 */
const pastePostprocess = (editor, args) => {
  const images = args.node.querySelectorAll('img[src^="data:"]');
  images.forEach((img) => {
    const base64 = (img.getAttribute('src') || '').split(',')[1] || '';
    // base64 encodes 3 bytes per 4 chars; subtract ~padding overhead
    const byteSize = Math.round((base64.length * 3) / 4);
    if (byteSize > MAX_IMAGE_BYTES) {
      img.remove();
      editor.notificationManager.open({
        text: `Image removed — it exceeds the 1 MB limit (${(byteSize / 1024 / 1024).toFixed(1)} MB). Please resize before inserting.`,
        type: 'error',
        timeout: 7000,
      });
    }
  });
};

const EqipRichTextEditor = ({ value, onChange, placeholder, readOnly, isA4Format = false }) => {
  const editorLoading = useContext(EditorLoadingContext);
  useEffect(() => {
    editorLoading?.onEditorMount?.();
  }, [editorLoading]);

  const handleEditorChange = (content, editor) => {
    if (!readOnly) {
      onChange(content);
    }
  };

  // A4 dimensions: 210mm x 297mm (approximately 794px x 1123px at 96dpi)
  const getEditorConfig = () => {
    const baseConfig = {
      selector: '#editor',
      plugins: [
        'advlist', 'anchor', 'autolink', 'charmap', 'code', 'fullscreen',
        'help', 'image', 'insertdatetime', 'link', 'lists', 'media',
        'preview', 'searchreplace', 'table', 'visualblocks',
      ],
      toolbar:
        'undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image',
      autoresize_overflow_padding: 50,

      // Image size enforcement
      automatic_uploads: true,
      images_upload_handler: imagesUploadHandler,
      paste_data_images: true,
      paste_postprocess: pastePostprocess,
    };

    if (isA4Format) {
      return {
        ...baseConfig,
        height: 800,
        content_css: 'document',
        content_style: `
          body {
            line-height: 1.5;
            width: 210mm;
            min-height: 297mm;
            margin: 0 auto;
            padding: 20mm 15mm;
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
          img { max-width: 100%; height: auto; }
          @media print {
            body {
              margin: 0;
              box-shadow: none;
            }
          }
        `,
      };
    }

    return {
      ...baseConfig,
      content_style: 'body { line-height: 1.5; } img { max-width: 100%; height: auto; }',
    };
  };

  return (
    <div className="eqip-rich-text-editor">
      <Editor
        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
        value={value || ''}
        disabled={readOnly}
        init={getEditorConfig()}
        onInit={() => editorLoading?.onEditorReady?.()}
        onEditorChange={handleEditorChange}
      />
      <style jsx>{`
        .eqip-rich-text-editor {
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 4px;
        }
        .tox-tinymce {
          border: 1px solid #ccc !important;
        }
        .tox .tox-menubar {
          background: #f4f4f4 !important;
        }
      `}</style>
    </div>
  );
};

export default EqipRichTextEditor;
